/**
 * Attribute groups: which characteristics an article of each kind is
 * described by, and where in the tree each group applies by default.
 *
 * Three panels, left to right, in the order the job is done: pick or create a
 * group, declare its attributes, then attach it to a node of the tree. The
 * attachment is inherited by the whole branch — set it on « Pneumatique » and
 * every tyre gets it — so the third panel lists the nodes that name the group
 * *themselves*, not every node that inherits it; the article screen says
 * where an inherited group came from.
 *
 * A required attribute counts towards a fiche's completion and blocks its
 * publication, exactly like a family's required attribute. Deactivating a
 * group is how an installation stops a half-designed set of inputs from
 * blocking the catalogue while it is finished.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  Input,
  Page,
  Select,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  CatalogApi,
  type AttributeDefinition,
  type AttributeGroup,
  type AttributeType,
  type WebCategory,
} from "../api";
import { NAMESPACE } from "../i18n";
import "./catalog.css";

const TYPES: AttributeType[] = ["text", "number", "choice", "boolean"];

/** The server's sentence when it has one: it names the field that is wrong. */
function reason(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback;
}

export function AttributesScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("catalog.category.write");

  const groupsFetcher = useCallback(() => api.attributeGroups(true), [api]);
  const groups = useResource(groupsFetcher);
  const categoriesFetcher = useCallback(() => api.categoryList(true), [api]);
  const categories = useResource(categoriesFetcher);

  const [selectedId, setSelectedId] = useState("");
  const [groupLabel, setGroupLabel] = useState("");

  const list = groups.data?.items ?? [];
  const selected: AttributeGroup | undefined =
    list.find((group) => group.id === selectedId) ?? list[0];

  function run(key: string, task: () => Promise<unknown>, done: string) {
    void action.run(key, async () => {
      try {
        await task();
        toast.show(done, { tone: "success" });
        groups.reload();
        categories.reload();
      } catch (error) {
        toast.show(reason(error, t("error.title")), { tone: "danger" });
      }
    });
  }

  return (
    <Page
      title={t("attributes.title")}
      description={t("attributes.description")}
      eyebrow={t("attributes.count", { count: list.length })}
    >
      <div className="hardauto-catalog__split">
        <Card title={t("attributes.groups")} compactTitle>
          <div className="hardauto-stack">
            {groups.loading ? (
              <p className="hardauto-cell__muted">{t("attributes.loading")}</p>
            ) : list.length === 0 ? (
              <div className="hardauto-empty-inline">
                <strong>{t("attributes.empty.title")}</strong>
                <p>{t("attributes.empty.body")}</p>
              </div>
            ) : (
              <ul className="hardauto-picklist" aria-label={t("attributes.groups")}>
                {list.map((group) => (
                  <li key={group.id}>
                    <button
                      type="button"
                      className="hardauto-picklist__item"
                      aria-current={group.id === selected?.id ? "true" : undefined}
                      onClick={() => setSelectedId(group.id)}
                    >
                      <span className="hardauto-picklist__title">{group.label}</span>
                      <span className="hardauto-cell__muted">
                        {t("attributes.attributeCount", { count: group.attributes.length })}
                      </span>
                      {group.is_active ? null : (
                        <Badge tone="neutral">{t("categories.inactive")}</Badge>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {mayWrite ? (
              <form
                className="hardauto-inline-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const label = groupLabel.trim();
                  if (!label) return;
                  run(
                    "group",
                    async () => {
                      const created = await api.createAttributeGroup({ label });
                      setGroupLabel("");
                      setSelectedId(created.id);
                    },
                    t("attributes.groupCreated"),
                  );
                }}
              >
                <div className="hardauto-inline-form__field">
                  <Field label={t("attributes.newGroup")}>
                    {(props) => (
                      <Input
                        {...props}
                        value={groupLabel}
                        placeholder={t("attributes.newGroupPlaceholder")}
                        onChange={(event) => setGroupLabel(event.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <div className="hardauto-inline-form__actions">
                  <Button type="submit" busy={action.isBusy("group")}>
                    {t("attributes.addGroup")}
                  </Button>
                </div>
              </form>
            ) : null}
          </div>
        </Card>

        {selected ? (
          <div className="hardauto-stack">
            <GroupAttributes
              key={selected.id}
              group={selected}
              api={api}
              mayWrite={mayWrite}
              busy={(key) => action.isBusy(key)}
              run={run}
            />
            <GroupAttachments
              group={selected}
              categories={categories.data?.items ?? []}
              api={api}
              mayWrite={mayWrite}
              busy={(key) => action.isBusy(key)}
              run={run}
            />
          </div>
        ) : null}
      </div>
    </Page>
  );
}

interface PanelProps {
  group: AttributeGroup;
  api: CatalogApi;
  mayWrite: boolean;
  busy: (key: string) => boolean;
  run: (key: string, task: () => Promise<unknown>, done: string) => void;
}

/** The selected group's attributes, and the form that adds one. */
function GroupAttributes({ group, api, mayWrite, busy, run }: PanelProps) {
  const { t } = useTranslation(NAMESPACE);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<AttributeType>("text");
  const [unit, setUnit] = useState("");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);
  const [filterable, setFilterable] = useState(false);

  const columns: Column<AttributeDefinition>[] = [
    {
      key: "label",
      header: t("attributes.column.label"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <span>{row.label}</span>
          <code className="hardauto-cell__muted">{row.key}</code>
        </div>
      ),
    },
    {
      key: "type",
      header: t("attributes.column.type"),
      render: (row) => (
        <span>
          {t(`attributes.type.${row.type}`)}
          {row.unit ? <span className="hardauto-cell__muted">{` · ${row.unit}`}</span> : null}
          {row.options.length > 0 ? (
            <span className="hardauto-cell__muted">{` · ${row.options.join(", ")}`}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "required",
      header: t("attributes.column.required"),
      render: (row) =>
        mayWrite ? (
          <Checkbox
            label={t("attributes.column.required")}
            checked={row.required}
            disabled={busy(row.id)}
            onChange={(event) =>
              run(
                row.id,
                () => api.updateAttribute(row.id, { required: event.target.checked }),
                t("attributes.saved"),
              )
            }
          />
        ) : row.required ? (
          <Badge tone="warning">{t("attributes.column.required")}</Badge>
        ) : null,
    },
    {
      key: "filterable",
      header: t("attributes.column.filterable"),
      render: (row) =>
        mayWrite ? (
          <Checkbox
            label={t("attributes.column.filterable")}
            checked={row.filterable}
            disabled={busy(row.id)}
            onChange={(event) =>
              run(
                row.id,
                () => api.updateAttribute(row.id, { filterable: event.target.checked }),
                t("attributes.saved"),
              )
            }
          />
        ) : row.filterable ? (
          <Badge tone="info">{t("attributes.column.filterable")}</Badge>
        ) : null,
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        mayWrite ? (
          <Button
            size="sm"
            variant="ghost"
            busy={busy(`remove-${row.id}`)}
            onClick={() =>
              run(`remove-${row.id}`, () => api.removeAttribute(row.id), t("attributes.removed"))
            }
          >
            {t("attributes.remove")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title={group.label}
      description={t("attributes.groupHint", { code: group.code })}
      flush
      actions={
        mayWrite ? (
          <Button
            size="sm"
            busy={busy(`toggle-${group.id}`)}
            onClick={() =>
              run(
                `toggle-${group.id}`,
                () => api.updateAttributeGroup(group.id, { is_active: !group.is_active }),
                t("attributes.saved"),
              )
            }
          >
            {group.is_active ? t("categories.deactivate") : t("categories.activate")}
          </Button>
        ) : undefined
      }
    >
      <DataTable<AttributeDefinition>
        caption={t("attributes.caption", { group: group.label })}
        columns={columns}
        rows={group.attributes}
        rowKey={(row) => row.id}
        loadingLabel={t("attributes.loading")}
        empty={{ title: t("attributes.none.title"), description: t("attributes.none.body") }}
      />
      {mayWrite ? (
        <form
          className="hardauto-inline-form hardauto-inline-form--padded"
          onSubmit={(event) => {
            event.preventDefault();
            if (!label.trim()) return;
            run(
              "attribute",
              async () => {
                await api.addAttribute(group.id, {
                  label: label.trim(),
                  type,
                  unit: unit.trim(),
                  options:
                    type === "choice"
                      ? options
                          .split(/[\n;]/)
                          .map((item) => item.trim())
                          .filter(Boolean)
                      : [],
                  required,
                  filterable,
                });
                setLabel("");
                setUnit("");
                setOptions("");
                setRequired(false);
                setFilterable(false);
              },
              t("attributes.added"),
            );
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("attributes.column.label")} required>
              {(props) => (
                <Input
                  {...props}
                  value={label}
                  placeholder={t("attributes.labelPlaceholder")}
                  onChange={(event) => setLabel(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("attributes.column.type")}>
              {(props) => (
                <Select
                  {...props}
                  value={type}
                  onChange={(event) => setType(event.target.value as AttributeType)}
                >
                  {TYPES.map((item) => (
                    <option key={item} value={item}>
                      {t(`attributes.type.${item}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("attributes.unit")}>
              {(props) => (
                <Input {...props} value={unit} onChange={(event) => setUnit(event.target.value)} />
              )}
            </Field>
          </div>
          {type === "choice" ? (
            <div className="hardauto-inline-form__field hardauto-inline-form__field--wide">
              <Field label={t("attributes.options")} hint={t("attributes.optionsHint")} required>
                {(props) => (
                  <Input
                    {...props}
                    value={options}
                    onChange={(event) => setOptions(event.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : null}
          <div className="hardauto-inline-form__field">
            <Checkbox
              label={t("attributes.column.required")}
              checked={required}
              onChange={(event) => setRequired(event.target.checked)}
            />
            <Checkbox
              label={t("attributes.column.filterable")}
              checked={filterable}
              onChange={(event) => setFilterable(event.target.checked)}
            />
          </div>
          <div className="hardauto-inline-form__actions">
            <Button type="submit" variant="primary" busy={busy("attribute")}>
              {t("attributes.add")}
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

/** Where the group applies by default: the nodes that name it themselves. */
function GroupAttachments({
  group,
  categories,
  api,
  mayWrite,
  busy,
  run,
}: PanelProps & { categories: WebCategory[] }) {
  const { t } = useTranslation(NAMESPACE);
  const [target, setTarget] = useState("");
  const attached = categories.filter((category) => category.attribute_group_id === group.id);
  const byId = new Map(categories.map((category) => [category.id, category]));

  /** « Pneumatique › OTR › Agricole », from the parent chain. */
  function trail(category: WebCategory): string {
    const labels: string[] = [];
    let current: WebCategory | undefined = category;
    while (current) {
      labels.unshift(current.label);
      current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    return labels.join(" › ");
  }

  const sorted = [...categories].sort((a, b) => a.path.localeCompare(b.path));

  return (
    <Card
      title={t("attributes.attachments")}
      description={t("attributes.attachmentsHint")}
      compactTitle
    >
      <div className="hardauto-stack">
        {attached.length === 0 ? (
          <p className="hardauto-cell__muted">{t("attributes.notAttached")}</p>
        ) : (
          <ul className="hardauto-media">
            {attached.map((category) => (
              <li key={category.id} className="hardauto-media__row">
                <span className="hardauto-media__label">{trail(category)}</span>
                {mayWrite ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    busy={busy(`detach-${category.id}`)}
                    onClick={() =>
                      run(
                        `detach-${category.id}`,
                        () => api.updateCategory(category.id, { clear_attribute_group: true }),
                        t("attributes.detached"),
                      )
                    }
                  >
                    {t("attributes.detach")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {mayWrite ? (
          <form
            className="hardauto-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!target) return;
              run(
                "attach",
                async () => {
                  await api.updateCategory(target, { attribute_group_id: group.id });
                  setTarget("");
                },
                t("attributes.attached"),
              );
            }}
          >
            <div className="hardauto-inline-form__field hardauto-inline-form__field--wide">
              <Field label={t("attributes.attachTo")}>
                {(props) => (
                  <Select
                    {...props}
                    value={target}
                    onChange={(event) => setTarget(event.target.value)}
                  >
                    <option value="">{t("categories.mapPlaceholder")}</option>
                    {sorted.map((category) => (
                      <option key={category.id} value={category.id}>
                        {`${"  ".repeat(category.depth - 1)}${category.label}`}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <div className="hardauto-inline-form__actions">
              <Button type="submit" busy={busy("attach")}>
                {t("attributes.attach")}
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </Card>
  );
}
